import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Github, Linkedin, 
  Phone, Mail, Lock, Check, Sparkles, MapPin, 
  BookOpen, Target, MessageSquare, Clock, ArrowUpRight
} from 'lucide-react';
import API_URL from '../../config';
import '../../pages/events/Events.css';
import '../../pages/events/EventsPrototype.css';

export default function EventParticipantProfileModal({ 
  participant: initialParticipant, 
  matchData, 
  eventSlug, 
  currentUserEmail, 
  onClose, 
  onConnect, 
  onAccept, 
  onReject, 
  onRemove,
  connectionStatus: initialStatus,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false
}) {
  const [participant, setParticipant] = useState(initialParticipant || {});
  const [status, setStatus] = useState(initialStatus || 'none');
  const [, setLoadingFull] = useState(false);
  const [showPrivacyNote, setShowPrivacyNote] = useState(false);

  useEffect(() => {
    if (initialParticipant) setParticipant(initialParticipant);
    if (initialStatus) setStatus(initialStatus);
  }, [initialParticipant, initialStatus]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      if (e.key === 'ArrowLeft' && hasPrev && onPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Fetch full details if participant email exists and slug is provided
  useEffect(() => {
    if (!participant?.email || !eventSlug) return;
    setLoadingFull(true);
    fetch(`${API_URL}/events-networking/${eventSlug}/participant/${participant.email}?viewer_email=${currentUserEmail || ''}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.participant) {
          setParticipant(prev => ({ ...prev, ...data.participant }));
          if (data.connectionStatus) {
            setStatus(data.connectionStatus);
          }
        }
      })
      .catch(err => console.error('Error fetching full participant profile:', err))
      .finally(() => setLoadingFull(false));
  }, [participant?.email, eventSlug, currentUserEmail]);

  if (!participant || !participant.email) return null;

  const isConnected = status === 'connected';
  const isPendingSent = status === 'pending_sent';
  const isPendingReceived = status === 'pending_received';
  const isSelf = status === 'self' || participant.email === currentUserEmail;

  const isExternal = Boolean(
    participant.is_external === true ||
    participant.is_external === 1 ||
    participant.is_external === '1' ||
    participant.is_external === 'true' ||
    (participant.college && participant.college.toLowerCase() !== 'vnr vjiet') ||
    (participant.email && !participant.email.endsWith('@vnrvjiet.in'))
  );

  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [String(parsed)];
    } catch {
      return String(val).split(',').map(s => s.trim()).filter(Boolean);
    }
  };

  const skills = safeArray(participant.skills);
  const techInterests = safeArray(participant.interestsTechnical || participant.technical_interests);
  const hobbies = safeArray(participant.hobbies);
  const networkingIntent = safeArray(participant.networking_intent);
  const lookingForSkills = safeArray(participant.looking_for_skills);
  const activities = participant.activities || [];
  const reasons = safeArray(matchData?.reasons || participant.match_reasons);
  const score = matchData?.score || participant.score || 85;

  const cleanPhone = String(participant.contact || participant.phone || '').replace(/\D/g, '');

  const getInitials = (nameStr) => {
    if (!nameStr) return '?';
    return nameStr.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Color scheme based on name
  const coverColors = ['blue', 'peach', 'lilac', 'green'];
  const coverColor = participant.cover || coverColors[Math.abs((participant.name || '').charCodeAt(0) || 0) % coverColors.length];

  return (
    <motion.div 
      className="vp-overlay open"
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    >
      <motion.aside 
        className="vp-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Participant profile of ${participant.name}`}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
      >
        {/* Drawer Head with continuous discovery Prev/Next & Close */}
        <div className="vp-drawer-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--vp-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Profile
            </span>
            {(onPrev || onNext) && (
              <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                <button 
                  className="vp-btn vp-btn-soft vp-btn-small" 
                  onClick={onPrev}
                  disabled={!hasPrev}
                  title="Previous attendee (← key)"
                  style={{ padding: '5px 10px' }}
                >
                  <ChevronLeft size={14} />
                </button>
                <button 
                  className="vp-btn vp-btn-soft vp-btn-small" 
                  onClick={onNext}
                  disabled={!hasNext}
                  title="Next attendee (→ key)"
                  style={{ padding: '5px 10px' }}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>

          <button 
            className="vp-icon-btn" 
            onClick={onClose} 
            aria-label="Close profile (Esc)"
            title="Close profile (Esc)"
            style={{ width: '36px', height: '36px', fontSize: '18px' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="vp-drawer-body">
          {/* Profile Hero with Persona and Initials */}
          <div className={`vp-profile-hero vp-person-cover ${coverColor}`}>
            <div className="vp-person-avatar">
              {participant.profile_pic ? (
                <img 
                  src={participant.profile_pic} 
                  alt={participant.name} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} 
                />
              ) : (
                getInitials(participant.name)
              )}
            </div>

            <div style={{ marginTop: '12px' }}>
              <span className="vp-origin-label" style={{ display: 'inline-block', marginBottom: '4px' }}>
                {isExternal ? 'Event guest · Outside institution' : 'VJLINK member · VNR VJIET'}
              </span>
              <h2 style={{ margin: '4px 0 6px', fontSize: '26px' }}>{participant.name}</h2>
              <div className="vp-person-meta" style={{ fontSize: '13px' }}>
                {isExternal ? (
                  [
                    participant.college || 'Outside Institution',
                    participant.department || participant.branch,
                    participant.year ? `Year ${participant.year}` : null
                  ].filter(Boolean).join(' · ')
                ) : (
                  [
                    'VNR VJIET',
                    participant.department || participant.branch,
                    participant.year,
                    participant.section ? `Sec ${participant.section}` : null
                  ].filter(Boolean).join(' · ')
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="vp-pill" style={{ background: 'var(--vp-accent)', color: 'var(--vp-accent-dark)', fontWeight: 800 }}>
                  <Sparkles size={12} /> {score}% match
                </span>
                {isConnected && (
                  <span className="vp-pill live">
                    <Check size={12} /> Connected
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Row */}
          {!isSelf && (
            <div className="vp-drawer-actions">
              {isConnected ? (
                <>
                  <button className="vp-btn vp-btn-soft" disabled style={{ flex: 1 }}>
                    <Check size={15} color="var(--vp-green)" /> Connected
                  </button>
                  {onRemove && (
                    <button 
                      className="vp-btn vp-btn-danger" 
                      style={{ flex: 0.6 }}
                      onClick={() => {
                        onRemove(participant);
                        setStatus('none');
                      }}
                    >
                      Disconnect
                    </button>
                  )}
                </>
              ) : isPendingSent ? (
                <button className="vp-btn vp-btn-soft" disabled style={{ flex: 1 }}>
                  <Clock size={15} /> Request sent
                </button>
              ) : isPendingReceived ? (
                <>
                  <button 
                    className="vp-btn vp-btn-lime" 
                    style={{ flex: 1 }}
                    onClick={() => {
                      if (onAccept) onAccept(participant);
                      setStatus('connected');
                    }}
                  >
                    <Check size={15} /> Accept request
                  </button>
                  <button 
                    className="vp-btn vp-btn-danger" 
                    style={{ flex: 0.6 }}
                    onClick={() => {
                      if (onReject) onReject(participant);
                      setStatus('none');
                    }}
                  >
                    Dismiss
                  </button>
                </>
              ) : (
                <button 
                  className="vp-btn vp-btn-lime" 
                  style={{ flex: 1 }}
                  onClick={() => {
                    if (onConnect) onConnect(participant);
                    setStatus('pending_sent');
                  }}
                >
                  ＋ Connect with {participant.name?.split(' ')[0] || 'Attendee'}
                </button>
              )}
              
              <button 
                className="vp-btn vp-btn-outline" 
                onClick={() => setShowPrivacyNote(p => !p)}
                title="View contact privacy policy"
              >
                Privacy rules
              </button>
            </div>
          )}

          {showPrivacyNote && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px 14px', 
              background: '#f5f8e9', 
              border: '1px solid #e3eccd', 
              borderRadius: '14px', 
              fontSize: '12px', 
              color: '#3d4d29', 
              lineHeight: 1.5 
            }}>
              <strong>Contact Privacy:</strong> Direct phone numbers and WhatsApp links remain strictly hidden until both participants accept a mutual connection.
            </div>
          )}

          {/* About / Bio */}
          {participant.bio && (
            <div className="vp-profile-block">
              <h3>About</h3>
              <p style={{ fontStyle: 'italic', fontSize: '13px', lineHeight: 1.6 }}>"{participant.bio}"</p>
            </div>
          )}

          {/* Why you match */}
          {(score || reasons.length > 0) && (
            <div className="vp-profile-block">
              <h3>Why you may click</h3>
              <div className="vp-match-insight" style={{ marginTop: 0 }}>
                <strong>Match rationale</strong>
                {reasons.length > 0 ? (
                  reasons.map((r, i) => (
                    <div key={i} style={{ marginBottom: i < reasons.length - 1 ? 4 : 0 }}>
                      • {r}
                    </div>
                  ))
                ) : (
                  <div>• Complementary skills and shared attendance at this event.</div>
                )}
              </div>
            </div>
          )}

          {/* Looking for / Networking Intent */}
          {networkingIntent.length > 0 && (
            <div className="vp-profile-block">
              <h3>What they’re looking for</h3>
              <div className="vp-tag-row">
                {networkingIntent.map((intent, i) => (
                  <span key={i} className="vp-skill-tag" style={{ background: '#eef0ff', color: '#4a56c4', fontWeight: 800 }}>
                    <Target size={12} style={{ display: 'inline', marginRight: 4 }} />
                    {intent}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Desired skills in collaborators */}
          {lookingForSkills.length > 0 && (
            <div className="vp-profile-block">
              <h3>Desired skills in teammates</h3>
              <div className="vp-tag-row">
                {lookingForSkills.map((s, i) => (
                  <span key={i} className="vp-skill-tag">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Skills & Technologies */}
          {skills.length > 0 && (
            <div className="vp-profile-block">
              <h3>Skills & technologies</h3>
              <div className="vp-tag-row">
                {skills.map((s, i) => (
                  <span key={i} className="vp-skill-tag" style={{ background: '#eff0ec', color: 'var(--vp-ink)', fontWeight: 700 }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Technical Interests */}
          {techInterests.length > 0 && (
            <div className="vp-profile-block">
              <h3>Technical interests</h3>
              <div className="vp-tag-row">
                {techInterests.map((t, i) => (
                  <span key={i} className="vp-skill-tag">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hobbies / Outside tech */}
          {hobbies.length > 0 && (
            <div className="vp-profile-block">
              <h3>Outside the technical world</h3>
              <div className="vp-tag-row">
                {hobbies.map((h, i) => (
                  <span key={i} className="vp-skill-tag" style={{ opacity: 0.9 }}>
                    {h}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Participating in */}
          {activities.length > 0 && (
            <div className="vp-profile-block">
              <h3>Participating in</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activities.map((act) => (
                  <div 
                    key={act.id} 
                    style={{ 
                      padding: '10px 14px', 
                      background: 'var(--vp-paper)', 
                      border: '1px solid var(--vp-line)', 
                      borderRadius: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--vp-ink)' }}>
                        {act.name}
                      </div>
                      {act.category && (
                        <div style={{ fontSize: '11px', color: 'var(--vp-muted)' }}>
                          {act.category}
                        </div>
                      )}
                    </div>
                    {act.venue && (
                      <div style={{ fontSize: '11px', color: 'var(--vp-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={11} /> {act.venue}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education / School / Junior College */}
          {(participant.school_name || participant.junior_college) && (
            <div className="vp-profile-block">
              <h3>School & Junior college</h3>
              <div style={{ fontSize: '13px', color: 'var(--vp-ink)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <BookOpen size={14} color="var(--vp-amber)" />
                <span>{[participant.school_name, participant.junior_college].filter(Boolean).join(' · ')}</span>
              </div>
            </div>
          )}

          {/* Contact Information & Privacy */}
          <div className="vp-profile-block">
            <h3>Contact information</h3>
            {isConnected ? (
              <div style={{ 
                background: 'var(--vp-green-soft)', 
                border: '1px solid rgba(27,128,89,0.2)', 
                borderRadius: '14px', 
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--vp-green)', fontSize: '13px', fontWeight: 700 }}>
                    <Phone size={16} />
                    <span>{participant.contact || participant.phone || 'Phone shared'}</span>
                  </div>
                  {cleanPhone && (
                    <a 
                      href={`https://wa.me/91${cleanPhone}`} 
                      target="_blank" 
                      rel="noreferrer"
                      className="vp-btn vp-btn-small"
                      style={{ 
                        background: '#1b8059', 
                        color: '#fff', 
                        padding: '6px 12px',
                        fontSize: '12px',
                        textDecoration: 'none'
                      }}
                    >
                      <MessageSquare size={13} /> WhatsApp
                    </a>
                  )}
                </div>
                {participant.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--vp-ink)' }}>
                    <Mail size={14} color="var(--vp-muted)" />
                    <span>{participant.email}</span>
                  </div>
                )}
              </div>
            ) : isSelf ? (
              <p style={{ fontSize: '13px', color: 'var(--vp-muted)' }}>
                This is your public networking profile visible to other attendees.
              </p>
            ) : (
              <div style={{ 
                background: 'var(--vp-paper)', 
                border: '1px dashed var(--vp-line)', 
                borderRadius: '14px', 
                padding: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Lock size={16} color="var(--vp-muted)" />
                <div style={{ fontSize: '12px', color: 'var(--vp-muted)', lineHeight: 1.5 }}>
                  Contact details remain protected. Phone number and WhatsApp contact become accessible only after both of you accept a connection request.
                </div>
              </div>
            )}

            {/* Social links */}
            {(participant.github || participant.linkedin) && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
                {participant.github && (
                  <a 
                    href={participant.github.startsWith('http') ? participant.github : `https://github.com/${participant.github}`} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="vp-btn vp-btn-outline vp-btn-small"
                    style={{ textDecoration: 'none' }}
                  >
                    <Github size={14} /> GitHub <ArrowUpRight size={12} />
                  </a>
                )}
                {participant.linkedin && (
                  <a 
                    href={participant.linkedin.startsWith('http') ? participant.linkedin : `https://linkedin.com/in/${participant.linkedin}`} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="vp-btn vp-btn-outline vp-btn-small"
                    style={{ textDecoration: 'none' }}
                  >
                    <Linkedin size={14} /> LinkedIn <ArrowUpRight size={12} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.aside>
    </motion.div>
  );
}
